import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, Check, Download, Loader2, RefreshCw, Sparkles, Upload, X } from 'lucide-react';
import type { PrintDesignRender, PrintPanel, PrintPlacement, PrintTemplate, PrintView } from '@wrapforce/shared';
import { activeOutlineViews, outlineViewFor, planPanelTiles, PRINT_PPI_GOOD, PRINT_PPI_MIN } from '@wrapforce/shared';
import { PLAN_DEFINITIONS, PRINT_CREDIT_COST } from '@wrapforce/shared/constants';
import { ApiError } from '@wrapforce/shared/errors';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useDebounce } from '@/lib/useDebounce';
import { useLocaleFormatters } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api-errors';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Input, Label } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  downloadPanelPrintFile, downloadPrintSpec, fetchPrintArtworkBlob, uploadPrintMedia, usePrintDesign, useMeasurePrintTemplate,
  useOutlineLayerUrls, useOutlinePrintTemplate, usePrintDesignRenders, useRenderPrintDesign, useSelectPrintOutline,
  useSavePrintTemplate, useUpdatePrintDesign,
} from './api';
import { PlacementEditor, type EditorArtwork } from './PlacementEditor';
import { RenderSetCarousel } from './RenderSetCarousel';
import {
  loadImage, measurePanelCoverage, type OutlineLayerImages, panelEffectivePpi, panelFixAdvice, panelsForView, rasterizeViewLayout, viewsWithPlacements,
} from './helpers';

const COVERAGE_KEY: Record<string, string> = {
  full: 'coverage.full',
  three_quarter: 'coverage.threeQuarter',
  half: 'coverage.half',
  spot: 'coverage.spot',
};

export function PrintDesignEditorPage() {
  const { t, i18n } = useTranslation('printWraps');
  const navigate = useNavigate();
  const { designId } = useParams<{ designId: string }>();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const hdAllowed = membership ? PLAN_DEFINITIONS[membership.plan].gates.hdRenders : false;
  const { formatDateTime } = useLocaleFormatters();

  const { data: design } = usePrintDesign(workspaceId, designId);
  const updateDesign = useUpdatePrintDesign(workspaceId, designId);
  const saveTemplate = useSavePrintTemplate(workspaceId, designId);
  const measureTemplate = useMeasurePrintTemplate(workspaceId, designId);
  const outlineTemplate = useOutlinePrintTemplate(workspaceId, designId);
  const selectOutline = useSelectPrintOutline(workspaceId, designId);
  const { confirm, ConfirmDialog } = useConfirm();
  const renderDesign = useRenderPrintDesign(workspaceId, designId);
  const { data: renders = [] } = usePrintDesignRenders(workspaceId, designId);
  const outlineUrls = useOutlineLayerUrls(workspaceId, designId, design?.template.template);

  const [enlargedRenderUrl, setEnlargedRenderUrl] = useState<string | null>(null);
  const [placements, setPlacements] = useState<PrintPlacement[] | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [quality, setQuality] = useState<'standard' | 'hd'>('standard');
  const [side, setSide] = useState<'driver' | 'passenger'>('driver');
  const sidePickedRef = useRef(false);
  const [templateEditOpen, setTemplateEditOpen] = useState(false);
  const [templateDraft, setTemplateDraft] = useState<PrintTemplate | null>(null);
  const [colorDraft, setColorDraft] = useState('');
  const [rollWidthIn, setRollWidthIn] = useState(54);
  const [bleedIn, setBleedIn] = useState(1);
  const [overlapIn, setOverlapIn] = useState(0.5);
  const [units, setUnits] = useState<'in' | 'mm' | 'both'>('both');
  const [specDownloading, setSpecDownloading] = useState(false);
  const [panelDownloading, setPanelDownloading] = useState<string | null>(null);
  const [artworkUrls, setArtworkUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (design && placements === null) {
      // The loaded placements are already saved, so the autosave below must
      // not PATCH them straight back on page open.
      lastSavedRef.current = JSON.stringify(design.placements);
      setPlacements(design.placements);
    }
  }, [design, placements]);

  useEffect(() => {
    if (design) setColorDraft(design.vehicleColor);
  }, [design?.id, design?.vehicleColor]);

  const artworkIdsKey = design?.artworks.map((a) => a.id).join(',') ?? '';
  useEffect(() => {
    if (!workspaceId || !design?.id || design.artworks.length === 0) {
      setArtworkUrls({});
      return;
    }
    let cancelled = false;
    const urls: Record<string, string> = {};
    void Promise.all(design.artworks.map(async (artwork) => {
      const blob = await fetchPrintArtworkBlob(workspaceId, design.id, artwork.id);
      if (blob) urls[artwork.id] = URL.createObjectURL(blob);
    })).then(() => {
      if (!cancelled) setArtworkUrls(urls);
    });
    return () => {
      cancelled = true;
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, design?.id, artworkIdsKey]);

  const prevOutlineStatusRef = useRef<'pending' | 'ready' | 'failed' | 'declined' | undefined>(undefined);
  useEffect(() => {
    const outline = design?.template.template.outline;
    if (outline?.status === 'ready' && prevOutlineStatusRef.current === 'pending') {
      // A restored 'ready' outline (a redraw that failed and fell back to
      // the previously active outline) carries the failure's error message -
      // notice that instead of the usual success toast, so the shop knows
      // the redraw itself didn't work even though they're not stuck.
      if (outline.error) toast.error(t('template.outline.toast.redrawFailedRestored'));
      else toast.success(t('template.outline.toast.ready'));
    }
    prevOutlineStatusRef.current = outline?.status;
  }, [design?.template.template.outline?.status, design?.template.template.outline?.error, t]);

  // Starts the outline automatically when the design has none yet - covers a
  // brand-new design (no outline at all), an old 'declined' row (the removed
  // default-outline choice), and a first-generation failure with nothing in
  // the library to fall back to. Only once per page load: a 'failed' status
  // with a library already stocked isn't retried automatically (the shop can
  // redraw from the template card instead), and this never fires again once
  // it has run so a failed retry doesn't loop.
  const autoStartedOutlineRef = useRef(false);
  useEffect(() => {
    if (!design || autoStartedOutlineRef.current) return;
    const outline = design.template.template.outline;
    const library = design.template.template.outlineLibrary;
    const noOutlineYet = !outline || outline.status === 'declined' || (outline.status === 'failed' && (!library || library.length === 0));
    if (!noOutlineYet) return;
    autoStartedOutlineRef.current = true;
    void outlineTemplate.mutateAsync({}).catch(() => {
      // Surfaced by the top progress panel's error state instead of a toast
      // here, since the shop is already looking at that panel.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design]);

  const debouncedPlacements = useDebounce(placements, 800);
  useEffect(() => {
    if (!debouncedPlacements || !designId) return;
    const serialized = JSON.stringify(debouncedPlacements);
    if (lastSavedRef.current === serialized) return;
    lastSavedRef.current = serialized;
    updateDesign.mutate({ placements: debouncedPlacements });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPlacements, designId]);

  // Until the user picks a side themselves, keep the render side pointed at
  // the side that has artwork. An explicit pick is respected even when that
  // side is empty - Generate is disabled with a hint instead.
  const placedViews = placements ? viewsWithPlacements(placements) : [];
  const driverHasArt = placedViews.includes('driver_side');
  const passengerHasArt = placedViews.includes('passenger_side');
  useEffect(() => {
    if (sidePickedRef.current) return;
    if (side === 'driver' && !driverHasArt && passengerHasArt) setSide('passenger');
    else if (side === 'passenger' && !passengerHasArt && driverHasArt) setSide('driver');
  }, [side, driverHasArt, passengerHasArt]);

  if (!design || !placements) {
    return <div className="p-6 text-sm text-muted">{t('common:nav.loading')}</div>;
  }

  const template = design.template.template;
  const templateSource = design.template.source;
  const verified = !!design.template.verifiedAt;
  const outline = template.outline;
  // Views the editor can actually draw with - the active ready outline, or
  // (during a redraw) the previous outline's views kept alongside the
  // 'pending' status - see activeOutlineViews. False only while there has
  // never been a usable outline yet (first generation).
  const drawableOutlineViews = activeOutlineViews(template);
  const hasDrawableOutline = !!drawableOutlineViews && Object.keys(drawableOutlineViews).length > 0;

  const sourceBadgeLabel = (): string => {
    if (verified && design.template.verifiedAt) return t('template.badge.verified', { date: formatDateTime(new Date(design.template.verifiedAt)) });
    if (templateSource === 'ai') return t('template.badge.aiEstimated');
    return t('template.badge.builtinEstimate');
  }

  const editorArtworks: EditorArtwork[] = design.artworks.map((a) => ({
    id: a.id, objectUrl: artworkUrls[a.id] ?? null, width: a.width, height: a.height,
  }));

  const handleArtworkUpload = async (files: File[]) => {
    if (!workspaceId || !designId || files.length === 0) return;
    setUploadingArtwork(true);
    try {
      let newest: string | null = null;
      for (const file of files) {
        const asset = await uploadPrintMedia(workspaceId, 'print_artwork', file, designId);
        newest = asset.id;
      }
      if (newest) await updateDesign.mutateAsync({ artworkMediaId: newest });
      toast.success(t('artwork.toast.uploaded'));
    } catch {
      toast.error(t('artwork.toast.uploadFailed'));
    } finally {
      setUploadingArtwork(false);
    }
  }

  const handleColorBlur = async () => {
    const trimmed = colorDraft.trim();
    if (trimmed === design.vehicleColor) return;
    try {
      await updateDesign.mutateAsync({ vehicleColor: trimmed });
    } catch {
      toast.error(t('template.toast.colorSaveFailed'));
    }
  }

  const handleMeasure = async () => {
    try {
      const result = await measureTemplate.mutateAsync();
      toast.success(result.cached ? t('template.toast.measureCached') : t('template.toast.measureSuccess'));
    } catch {
      toast.error(t('template.toast.measureFailed'));
    }
  }

  // Starts the AI outline: either the automatic first generation (free, see
  // the auto-start effect above) or a manual retry from the progress panel's
  // error state after a first-generation failure.
  const handleStartOutline = async () => {
    try {
      await outlineTemplate.mutateAsync({});
    } catch (err) {
      toast.error(t('template.outline.toast.startFailed'), { description: apiErrorMessage(err) });
    }
  }

  // Free, immediate switch to a specific previously generated outline
  // (outlineId matching a template.outlineLibrary entry's id) - used by the
  // template card's outline picker.
  const handleSelectOutlineLibraryEntry = async (outlineId: string) => {
    try {
      await selectOutline.mutateAsync(outlineId);
    } catch {
      toast.error(t('template.outline.picker.switchFailed'));
    }
  }

  // The template card's outline picker, once a library entry already exists -
  // confirms first since placements may shift between differently-sized
  // outlines, then switches for free.
  const handleSwitchOutline = async (outlineId: string) => {
    const ok = await confirm({
      title: t('template.outline.picker.switchConfirm.title'),
      description: t('template.outline.picker.switchConfirm.description'),
      confirmLabel: t('template.outline.picker.switchConfirm.confirmLabel'),
    });
    if (!ok) return;
    await handleSelectOutlineLibraryEntry(outlineId);
  }

  const handleRedrawOutline = async () => {
    const ok = await confirm({
      title: t('template.outline.redrawConfirm.title'),
      description: t('template.outline.redrawConfirm.description', { count: PRINT_CREDIT_COST.outline }),
      confirmLabel: t('template.outline.redrawConfirm.confirmLabel'),
    });
    if (!ok) return;
    try {
      await outlineTemplate.mutateAsync({ redraw: true });
    } catch {
      toast.error(t('template.outline.toast.startFailed'));
    }
  }

  // Loads this view's outline layers (see PrintCanvas/helpers.ts) as decoded
  // images, ready to pass into rasterizeViewLayout/measurePanelCoverage/
  // cropPanelArtwork - undefined when the view has no outline, it isn't
  // ready yet, or its layer URLs haven't loaded (see useOutlineLayerUrls).
  const loadOutlineImagesForView = async (view: PrintView): Promise<OutlineLayerImages | undefined> => {
    const info = outlineViewFor(view);
    if (!info) return undefined;
    const views = activeOutlineViews(template);
    const data = views?.[info.source];
    if (!data) return undefined;
    const imageUrl = outlineUrls[data.imageMediaId];
    const maskUrl = outlineUrls[data.maskMediaId];
    const linesUrl = outlineUrls[data.linesMediaId];
    if (!imageUrl || !maskUrl || !linesUrl) return undefined;
    const [image, mask, lines] = await Promise.all([loadImage(imageUrl), loadImage(maskUrl), loadImage(linesUrl)]);
    return { image, mask, lines, mirror: info.mirror };
  }

  const openTemplateEdit = () => {
    setTemplateDraft(template);
    setTemplateEditOpen(true);
  }

  const handleSaveTemplate = async (markVerified = false) => {
    if (!templateDraft) return;
    try {
      await saveTemplate.mutateAsync({ template: templateDraft, verified: markVerified || undefined });
      toast.success(t('template.toast.saved'));
      setTemplateEditOpen(false);
    } catch {
      toast.error(t('template.toast.saveFailed'));
    }
  }

  const handleMarkVerified = async () => {
    try {
      await saveTemplate.mutateAsync({ template, verified: true });
      toast.success(t('template.toast.verified'));
    } catch {
      toast.error(t('template.toast.saveFailed'));
    }
  }

  const viewsInUse = viewsWithPlacements(placements);
  const hasDriverSide = viewsInUse.includes('driver_side');
  const hasPassengerSide = viewsInUse.includes('passenger_side');
  const hasAnySidePlacement = hasDriverSide || hasPassengerSide;
  const chosenSideHasArtwork = side === 'driver' ? hasDriverSide : hasPassengerSide;
  const effectiveSide: 'driver' | 'passenger' | undefined = hasAnySidePlacement ? side : undefined;

  const collectLayoutMediaIds = async (forAi: boolean): Promise<string[]> => {
    if (!workspaceId || !designId) return [];
    const ids: string[] = [];
    for (const view of viewsInUse.slice(0, 6)) {
      const outline = await loadOutlineImagesForView(view);
      const blob = await rasterizeViewLayout({ template, view, placements, artworkUrls, forAi, outline });
      const asset = await uploadPrintMedia(workspaceId, 'print_layout', blob, designId);
      ids.push(asset.id);
    }
    return ids;
  }

  // A render set always covers the chosen side, front and rear - even a view
  // with no placements still gets a plain-vehicle layout, so the set is
  // always three views. Not gated on viewsInUse like the photo-mode path
  // above.
  const collectSetLayouts = async (): Promise<{ side: string; front: string; rear: string } | null> => {
    if (!workspaceId || !designId || !effectiveSide) return null;
    const sideView: PrintView = effectiveSide === 'driver' ? 'driver_side' : 'passenger_side';
    const sideDims = template.views[sideView];
    if (!sideDims) return null;
    // Front and rear must be framed exactly like the side view - same
    // aspect ratio and pixels-per-inch - or the image model follows their
    // own near-square input dims and renders them ~1:1. Gemini's image
    // output only supports fixed ratios up to 21:9, so when the side view is
    // wider than that, pad all three canvases vertically to exactly 21:9
    // instead of leaving the server to pick "nearest" for a much wider
    // source; otherwise the frame is just the side view's own dims.
    let frameHeightIn = sideDims.heightIn;
    if (sideDims.widthIn / sideDims.heightIn > 21 / 9) {
      frameHeightIn = sideDims.widthIn / (21 / 9);
    }
    const frameIn = { widthIn: sideDims.widthIn, heightIn: frameHeightIn };
    const ids: string[] = [];
    for (const view of [sideView, 'front', 'rear'] as const) {
      const outline = await loadOutlineImagesForView(view);
      const blob = await rasterizeViewLayout({ template, view, placements, artworkUrls, forAi: true, frameIn, outline });
      const asset = await uploadPrintMedia(workspaceId, 'print_layout', blob, designId);
      ids.push(asset.id);
    }
    return { side: ids[0]!, front: ids[1]!, rear: ids[2]! };
  }

  const collectPanelCoverage = async (): Promise<{ view: PrintView; key: string; coveredFraction: number }[]> => {
    const rows: { view: PrintView; key: string; coveredFraction: number }[] = [];
    try {
      for (const view of viewsInUse) {
        const outline = await loadOutlineImagesForView(view);
        const perPanel = await measurePanelCoverage({ template, view, placements, artworkUrls, outline });
        for (const { key, coveredFraction } of perPanel) {
          rows.push({ view, key, coveredFraction: Math.round(coveredFraction * 100) / 100 });
        }
      }
    } catch (err) {
      // Coverage is a nice-to-have for the render prompt, not required for
      // the render itself - never block generating over a measurement bug.
      console.warn('measurePanelCoverage failed, sending the render without panel coverage', err);
      return [];
    }
    return rows;
  }

  const generateDisabled = viewsInUse.length === 0 || !hasAnySidePlacement || !chosenSideHasArtwork;

  const handleGenerate = async () => {
    if (viewsInUse.length === 0) {
      toast.error(t('generate.toast.noPlacements'));
      return;
    }
    if (!hasAnySidePlacement) {
      toast.error(t('generate.toast.noSidePlacement'));
      return;
    }
    setGenerating(true);
    try {
      const panelCoverage = await collectPanelCoverage();
      const setLayouts = await collectSetLayouts();
      if (!setLayouts) {
        toast.error(t('generate.toast.noSidePlacement'));
        return;
      }
      await renderDesign.mutateAsync({
        quality,
        angle: 'side',
        // Required by the schema but unused by the server for a set -
        // reuse the same three uploads so nothing extra is rasterized.
        layoutMediaIds: [setLayouts.side, setLayouts.front, setLayouts.rear],
        useVehiclePhoto: false,
        side: effectiveSide,
        setLayouts,
        panelCoverage: panelCoverage.length > 0 ? panelCoverage : undefined,
      });
      toast.info(t('generate.toast.started'));
    } catch {
      toast.error(t('generate.toast.failed'));
    } finally {
      setGenerating(false);
    }
  }

  const handleSetFinal = async () => {
    try {
      await updateDesign.mutateAsync({ status: 'final' });
      toast.success(t('generate.toast.markedFinal'));
    } catch {
      toast.error(t('generate.toast.markFinalFailed'));
    }
  }

  const handleDownloadSpec = async () => {
    if (!workspaceId || !designId) return;
    setSpecDownloading(true);
    try {
      const layoutMediaIds = await collectLayoutMediaIds(false);
      await downloadPrintSpec(workspaceId, designId, { rollWidthIn, bleedIn, overlapIn, units, layoutMediaIds });
    } catch {
      toast.error(t('spec.toast.downloadFailed'));
    } finally {
      setSpecDownloading(false);
    }
  }

  const handleDownloadPanel = async (panel: PrintPanel) => {
    if (!workspaceId || !designId) return;
    const key = `${panel.view}:${panel.key}`;
    setPanelDownloading(key);
    try {
      await downloadPanelPrintFile(workspaceId, designId, panel.view, panel.key, { rollWidthIn, bleedIn, overlapIn });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        toast.error(t('spec.toast.printServerBusy'));
      } else {
        toast.error(t('spec.toast.panelDownloadFailed'));
      }
    } finally {
      setPanelDownloading(null);
    }
  }

  const anyPending = renders.some((r) => r.status === 'pending');

  // Groups the three renders of one "Generate" click (side, three-quarter
  // front, three-quarter rear) into one carousel; a null batchId (a legacy
  // render, or a single customer-photo-mode render) is its own one-item
  // group, keyed by its own id so it never merges with another null-batch
  // render. Newest group first, by that group's most recent render.
  const renderGroups = (() => {
    const map = new Map<string, { key: string; renders: PrintDesignRender[] }>();
    for (const render of renders) {
      const key = render.batchId ?? render.id;
      const group = map.get(key);
      if (group) group.renders.push(render);
      else map.set(key, { key, renders: [render] });
    }
    return Array.from(map.values()).sort((a, b) => {
      const aTime = Math.max(...a.renders.map((r) => new Date(r.createdAt).getTime()));
      const bTime = Math.max(...b.renders.map((r) => new Date(r.createdAt).getTime()));
      return bTime - aTime;
    });
  })();

  return (
    <div className="flex flex-col gap-4 p-6">
      <ConfirmDialog />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(returnTo ?? '/print-wraps')}
          className="flex items-center gap-1 text-xs font-semibold text-muted hover:text-ink"
        >
          <ArrowLeft size={14} /> {t('editor.back')}
        </button>
        <div className="ml-2">
          <h1 className="text-lg font-bold text-ink">{design.name}</h1>
          <p className="text-sm text-muted">{design.vehicleLabel} · {t(COVERAGE_KEY[design.coverageLevel] ?? 'coverage.full')}</p>
        </div>
      </div>

      {outline?.status === 'pending' ? (
        <OutlineProgressBanner outline={outline} vehicleLabel={design.vehicleLabel} isRedraw={hasDrawableOutline} />
      ) : outline?.status === 'failed' ? (
        <OutlineErrorBanner outline={outline} onRetry={handleStartOutline} retrying={outlineTemplate.isPending} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t('template.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <span className="w-fit bg-surface-2 px-2 py-1 text-[11px] font-semibold text-ink">{sourceBadgeLabel()}</span>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted">{t('template.lengthIn')}</span> {template.overall.lengthIn}&quot;</div>
              <div><span className="text-muted">{t('template.widthIn')}</span> {template.overall.widthIn}&quot;</div>
              <div><span className="text-muted">{t('template.heightIn')}</span> {template.overall.heightIn}&quot;</div>
              <div><span className="text-muted">{t('template.wheelbaseIn')}</span> {template.overall.wheelbaseIn}&quot;</div>
            </div>
            <p className="text-[11px] text-muted">
              {template.features ? t('template.featuresHintMeasured') : t('template.featuresHintEstimated')}
            </p>
            <div className="flex flex-wrap gap-2">
              {template.outline?.status === 'declined' ? (
                <Button size="sm" variant="outline" disabled={templateSource === 'ai' && !!template.features} loading={measureTemplate.isPending} onClick={handleMeasure}>
                  <Sparkles size={13} className="mr-1" /> {t('template.getAiMeasurements', { count: PRINT_CREDIT_COST.measure })}
                </Button>
              ) : null}
              <Button size="sm" variant="outline" disabled={verified} loading={saveTemplate.isPending} onClick={handleMarkVerified}>
                {t('template.markVerified')}
              </Button>
              <Button size="sm" variant="ghost" onClick={openTemplateEdit}>
                {t('common:actions.edit')}
              </Button>
            </div>

            {outline ? (
              <div className="flex flex-col gap-2 border-t border-border pt-3">
                {template.outlineLibrary && template.outlineLibrary.length > 1 ? (
                  <div className="flex min-w-0 flex-col gap-1">
                    <Label htmlFor="print-outline-picker">{t('template.outline.picker.label')}</Label>
                    <select
                      id="print-outline-picker"
                      className="h-9 w-full min-w-0 truncate border border-border bg-surface px-2 text-sm"
                      disabled={outline.status === 'pending' || selectOutline.isPending}
                      value={outline.activeOutlineId ?? ''}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        if (nextId && nextId !== (outline.activeOutlineId ?? '')) void handleSwitchOutline(nextId);
                      }}
                    >
                      {template.outlineLibrary.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {t('template.outline.picker.libraryOption', { date: formatDateTime(new Date(entry.createdAt)) })}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {/* A stocked library means the first (free) generation already
                    happened, so this button always costs credits from then on;
                    with an empty library it's still the free first attempt, so
                    it starts (not redraws) and shows "free" instead of a cost. */}
                {template.outlineLibrary && template.outlineLibrary.length > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-fit max-w-full"
                    disabled={outline.status === 'pending'}
                    loading={outlineTemplate.isPending}
                    onClick={handleRedrawOutline}
                  >
                    <Sparkles size={13} className="mr-1" /> {t('template.outline.picker.drawNew', { count: PRINT_CREDIT_COST.outline })}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-fit max-w-full"
                    disabled={outline.status === 'pending'}
                    loading={outlineTemplate.isPending}
                    onClick={handleStartOutline}
                  >
                    <Sparkles size={13} className="mr-1" /> {t('template.outline.picker.drawNewFree')}
                  </Button>
                )}
              </div>
            ) : null}

            {templateEditOpen && templateDraft ? (
              <div className="flex flex-col gap-2 border-t border-border pt-3">
                <div className="grid grid-cols-2 gap-2">
                  {(['lengthIn', 'widthIn', 'heightIn', 'wheelbaseIn'] as const).map((key) => (
                    <div key={key} className="flex flex-col gap-1">
                      <Label>{t(`template.${key}`)}</Label>
                      <Input
                        type="number"
                        value={templateDraft.overall[key]}
                        onChange={(e) => setTemplateDraft({ ...templateDraft, overall: { ...templateDraft.overall, [key]: Number(e.target.value) } })}
                      />
                    </div>
                  ))}
                </div>
                <Label className="mt-1">{t('template.panelsLabel')}</Label>
                <div className="flex max-h-60 flex-col gap-1.5 overflow-y-auto">
                  {templateDraft.panels.map((panel, idx) => (
                    <div key={`${panel.view}:${panel.key}`} className="grid grid-cols-4 items-center gap-1.5 text-xs">
                      <span className="col-span-2 truncate text-muted">{panel.label}</span>
                      <Input
                        type="number"
                        className="h-8"
                        value={panel.w}
                        onChange={(e) => {
                          const panels = [...templateDraft.panels];
                          panels[idx] = { ...panel, w: Number(e.target.value) };
                          setTemplateDraft({ ...templateDraft, panels });
                        }}
                      />
                      <Input
                        type="number"
                        className="h-8"
                        value={panel.h}
                        onChange={(e) => {
                          const panels = [...templateDraft.panels];
                          panels[idx] = { ...panel, h: Number(e.target.value) };
                          setTemplateDraft({ ...templateDraft, panels });
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" loading={saveTemplate.isPending} onClick={() => handleSaveTemplate(false)}>
                    {t('common:actions.save')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setTemplateEditOpen(false)}>
                    {t('common:actions.cancel')}
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5 border-t border-border pt-3">
              <Label htmlFor="print-vehicle-color">{t('template.colorLabel')}</Label>
              <Input
                id="print-vehicle-color"
                value={colorDraft}
                onChange={(e) => setColorDraft(e.target.value)}
                onBlur={handleColorBlur}
                placeholder={t('template.colorPlaceholder')}
              />
            </div>

          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('artwork.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {design.artworks.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {design.artworks.map((artwork) => (
                  <img
                    key={artwork.id}
                    src={artworkUrls[artwork.id] ?? artwork.thumbUrl ?? artwork.url}
                    alt=""
                    className="aspect-square w-full border border-border object-contain"
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted">{t('artwork.empty')}</p>
            )}
            <label className="flex w-fit cursor-pointer items-center gap-1.5 border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-surface-2">
              <Upload size={13} /> {t('artwork.uploadButton')}
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                disabled={uploadingArtwork}
                className="hidden"
                onChange={(e) => {
                  // Copy the files out before clearing the input: clearing it
                  // empties the live FileList.
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  if (files.length > 0) void handleArtworkUpload(files);
                }}
              />
            </label>
            <p className="text-[11px] text-muted">{t('artwork.vectorHint')}</p>
            {uploadingArtwork ? <span className="text-xs text-muted">{t('artwork.uploading')}</span> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('spec.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col gap-1">
                <Label>{t('spec.rollWidth')}</Label>
                <Input type="number" min={24} max={64} value={rollWidthIn} onChange={(e) => setRollWidthIn(Number(e.target.value))} />
              </div>
              <div className="flex flex-col gap-1">
                <Label>{t('spec.bleed')}</Label>
                <Input type="number" min={0} max={4} step={0.25} value={bleedIn} onChange={(e) => setBleedIn(Number(e.target.value))} />
              </div>
              <div className="flex flex-col gap-1">
                <Label>{t('spec.overlap')}</Label>
                <Input type="number" min={0} max={4} step={0.25} value={overlapIn} onChange={(e) => setOverlapIn(Number(e.target.value))} />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('spec.units')}</Label>
              <select value={units} onChange={(e) => setUnits(e.target.value as typeof units)} className="h-9 border border-border bg-surface px-2 text-sm">
                <option value="in">{t('spec.unitsIn')}</option>
                <option value="mm">{t('spec.unitsMm')}</option>
                <option value="both">{t('spec.unitsBoth')}</option>
              </select>
            </div>
            <Button size="sm" loading={specDownloading} onClick={handleDownloadSpec}>
              <Download size={13} className="mr-1" /> {t('spec.downloadPdf')}
            </Button>

            {viewsInUse.length > 0 ? (
              <div className="flex flex-col gap-1.5 border-t border-border pt-3">
                <Label>{t('spec.panelDownloadsLabel')}</Label>
                <p className="text-[11px] text-muted">{t('spec.panelDownloadsHint')}</p>
                {templateSource !== 'uploaded' && !verified ? (
                  <p className="text-[11px] text-warning">{t('spec.templateEstimateWarning')}</p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {viewsInUse.flatMap((view) => panelsForView(template, view)).map((panel) => {
                    const key = `${panel.view}:${panel.key}`;
                    const tileCount = planPanelTiles(panel, { rollWidthIn, bleedIn, overlapIn }).tiles.length;
                    const quality = panelEffectivePpi(panel, placements ?? [], design.artworks);
                    let qualityClass = 'text-muted';
                    let qualityText: string | null = null;
                    const isWarning = quality.kind === 'raster' && quality.ppi < PRINT_PPI_GOOD;
                    if (quality.kind === 'vector') {
                      qualityText = t('spec.panelQualityVector');
                    } else if (quality.kind === 'raster') {
                      if (quality.ppi >= PRINT_PPI_GOOD) {
                        qualityText = t('spec.panelQualitySharp');
                      } else {
                        qualityClass = 'text-warning';
                        qualityText = quality.ppi >= PRINT_PPI_MIN ? t('spec.panelQualityLow') : t('spec.panelQualityTooLow');
                      }
                    }
                    const advice = isWarning ? panelFixAdvice(panel, placements ?? [], design.artworks) : null;
                    const formatPx = (n: number) => new Intl.NumberFormat(i18n.language).format(n);
                    return (
                      <div key={key} className="flex max-w-[260px] flex-col gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-auto flex-col items-start gap-0.5 py-1.5 text-left font-bold"
                          disabled={panelDownloading === key}
                          loading={panelDownloading === key}
                          onClick={() => handleDownloadPanel(panel)}
                        >
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <Download size={12} /> {panel.label}
                            {tileCount > 1 ? (
                              <span className="font-normal text-muted">· {t('spec.panelTileCount', { count: tileCount })}</span>
                            ) : null}
                          </span>
                          {qualityText ? (
                            <span className={`font-normal ${qualityClass}`}>{qualityText}</span>
                          ) : null}
                        </Button>
                        {advice ? (
                          <details className="text-[11px]">
                            <summary className="cursor-pointer font-semibold text-warning">{t('spec.panelFixSummary')}</summary>
                            <div className="mt-1 flex flex-col gap-1 text-muted">
                              <p>{t('spec.panelFixVector')}</p>
                              {advice.kind === 'vector-only' ? (
                                <p>{t('spec.panelFixVectorOnly')}</p>
                              ) : (
                                <>
                                  <p>{t('spec.panelFixBigger', { width: formatPx(advice.widthPx), height: formatPx(advice.heightPx) })}</p>
                                  {advice.kind === 'bigger-image-limited' ? <p>{t('spec.panelFixLimited')}</p> : null}
                                </>
                              )}
                              <p>{t('spec.panelFixTooSmallLogo')}</p>
                              <p>{t('spec.panelFixDownloadNow')}</p>
                            </div>
                          </details>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {hasDrawableOutline ? (
        <PlacementEditor
          template={template}
          artworks={editorArtworks}
          placements={placements}
          resetKey={design.id}
          outlineUrls={outlineUrls}
          onChange={setPlacements}
        />
      ) : (
        <OutlineFirstGenPlaceholder outline={outline} vehicleLabel={design.vehicleLabel} onRetry={handleStartOutline} retrying={outlineTemplate.isPending} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('generate.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <select value={quality} onChange={(e) => setQuality(e.target.value as 'standard' | 'hd')} className="h-9 border border-border bg-surface px-2 text-sm">
              <option value="standard">{t('generate.qualityStandard', { count: PRINT_CREDIT_COST.setStandard })}</option>
              <option value="hd" disabled={!hdAllowed} title={!hdAllowed ? t('generate.qualityHdLocked') : undefined}>
                {t('generate.qualityHd', { count: PRINT_CREDIT_COST.setHd })}
              </option>
            </select>
            {hasAnySidePlacement ? (
              <select
                value={side}
                onChange={(e) => {
                  sidePickedRef.current = true;
                  setSide(e.target.value as typeof side);
                }}
                className="h-9 border border-border bg-surface px-2 text-sm">
                <option value="driver">{t('generate.sideDriver')}</option>
                <option value="passenger">{t('generate.sidePassenger')}</option>
              </select>
            ) : null}
            <Button size="sm" disabled={generateDisabled} loading={generating || renderDesign.isPending} onClick={handleGenerate}>
              <Sparkles size={13} className="mr-1" />
              {t('generate.generateSetButton', { count: quality === 'hd' ? PRINT_CREDIT_COST.setHd : PRINT_CREDIT_COST.setStandard })}
            </Button>
            {design.status !== 'final' ? (
              <Button size="sm" variant="outline" disabled={renders.every((r) => r.status !== 'succeeded')} onClick={handleSetFinal}>
                {t('generate.setFinal')}
              </Button>
            ) : null}
          </div>
          {!hdAllowed ? <p className="text-[11px] text-muted">{t('generate.qualityHdLocked')}</p> : null}
          {!hasAnySidePlacement ? (
            <p className="text-[11px] text-muted">{t('generate.toast.noSidePlacement')}</p>
          ) : !chosenSideHasArtwork ? (
            <p className="text-[11px] text-muted">
              {t(side === 'driver' ? 'generate.noArtworkOnDriverSide' : 'generate.noArtworkOnPassengerSide')}
            </p>
          ) : null}
          {anyPending ? <p className="text-xs text-muted">{t('generate.polling')}</p> : null}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {renderGroups.map((group) => (
              <RenderSetCarousel
                key={group.key}
                workspaceId={workspaceId!}
                designId={designId!}
                designName={design.name}
                renders={group.renders}
                onOpenRender={setEnlargedRenderUrl}
              />
            ))}
            {renderGroups.length === 0 ? <p className="text-sm text-muted">{t('generate.noRenders')}</p> : null}
          </div>
          <p className="text-xs text-muted">{t('generate.printFromSpecNote')}</p>
        </CardContent>
      </Card>
      {enlargedRenderUrl ? <RenderLightbox url={enlargedRenderUrl} onClose={() => setEnlargedRenderUrl(null)} /> : null}
    </div>
  );
}

// The five steps a generation job goes through server side, in order - used
// to render the checklist and to estimate progress when the server hasn't
// sent a `progress` block yet (an older/just-restarted job).
const OUTLINE_STEP_KEYS = ['measure', 'driver_side', 'front', 'rear', 'roof'] as const;
type OutlineStepKey = (typeof OUTLINE_STEP_KEYS)[number];
type OutlineProgress = NonNullable<NonNullable<PrintTemplate['outline']>['progress']>;

// Fraction complete (0..1, never reaching 1 here - only the 'ready'/'failed'
// status does that) from the server's real step statuses: each finished step
// is a full share, and the currently running step eases smoothly toward its
// own share over an assumed ~90s/stepCount so the bar keeps moving instead of
// sitting frozen for however long that step actually takes.
function progressFractionFromSteps(progress: OutlineProgress, nowMs: number): number {
  const total = progress.steps.length || OUTLINE_STEP_KEYS.length;
  const perStepSec = 90 / total;
  const startedAtMs = new Date(progress.startedAt).getTime();
  const doneCount = progress.steps.filter((s) => s.status === 'done').length;
  const runningIdx = progress.steps.findIndex((s) => s.status === 'running' || s.status === 'retrying');
  let fraction = doneCount / total;
  if (runningIdx !== -1) {
    const elapsedInStepSec = Math.max(0, (nowMs - startedAtMs) / 1000 - doneCount * perStepSec);
    const ease = 1 - Math.exp(-elapsedInStepSec / perStepSec);
    fraction += Math.min(ease, 0.95) / total;
  }
  return Math.min(0.99, fraction);
}

// Purely time-based fallback for when the server hasn't reported `progress`
// yet - asymptotically approaches (but never reaches) 100% over an assumed
// 90s total, so it never looks frozen and never falsely claims completion.
function progressFractionFromElapsed(startedAtMs: number, nowMs: number): number {
  const elapsedSec = Math.max(0, (nowMs - startedAtMs) / 1000);
  return Math.min(0.95, 1 - Math.exp(-elapsedSec / 90));
}

// Shared body for the top progress banner and the first-generation
// placeholder below - title, progress bar, step checklist, elapsed time and
// reassurance copy. `aria-live="polite"` lives on the containing Card so
// screen readers hear updates without being interrupted mid-sentence.
function OutlineProgressContent({ outline, vehicleLabel, isRedraw }: {
  outline: NonNullable<PrintTemplate['outline']>;
  vehicleLabel: string;
  isRedraw: boolean;
}) {
  const { t } = useTranslation('printWraps');
  const startedAtMs = outline.progress ? new Date(outline.progress.startedAt).getTime() : new Date(outline.requestedAt).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const elapsedSec = Math.max(0, Math.floor((now - startedAtMs) / 1000));
  const minutes = Math.floor(elapsedSec / 60);
  const seconds = (elapsedSec % 60).toString().padStart(2, '0');
  const fraction = outline.progress ? progressFractionFromSteps(outline.progress, now) : progressFractionFromElapsed(startedAtMs, now);
  const pct = Math.round(fraction * 100);
  const stepStatusByKey = new Map((outline.progress?.steps ?? []).map((s) => [s.key, s.status]));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-ink">
          {isRedraw ? t('template.outline.pending.redrawTitle', { vehicle: vehicleLabel }) : t('template.outline.pending.title', { vehicle: vehicleLabel })}
        </p>
        {outline.free ? (
          <span className="w-fit bg-surface-2 px-1.5 py-0.5 text-[10px] font-semibold text-ink">{t('template.outline.pending.freeBadge')}</span>
        ) : null}
      </div>
      <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} className="h-2 w-full overflow-hidden bg-surface-2">
        <div
          className="h-full bg-accent motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {OUTLINE_STEP_KEYS.map((key: OutlineStepKey) => {
          const status = stepStatusByKey.get(key) ?? 'waiting';
          return (
            <div key={key} className="flex items-center gap-1.5 text-xs text-muted">
              {status === 'done' ? (
                <Check size={13} className="shrink-0 text-accent" aria-hidden />
              ) : status === 'running' || status === 'retrying' ? (
                <Loader2 size={13} className="shrink-0 motion-safe:animate-spin" aria-hidden />
              ) : (
                <span className="h-[13px] w-[13px] shrink-0 rounded-full border border-border" aria-hidden />
              )}
              <span className={status === 'done' ? 'text-ink' : undefined}>
                {t(`template.outline.pending.steps.${key}`)}
                {status === 'retrying' ? ` · ${t('template.outline.pending.stepStatus.retrying')}` : ''}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted">
        {t('template.outline.pending.elapsed', { time: `${minutes}:${seconds}` })} · {t('template.outline.pending.estimate')}
      </p>
      <p className="max-w-md text-xs text-muted">{isRedraw ? t('template.outline.pending.redrawNote') : t('template.outline.pending.note')}</p>
    </div>
  );
}

// Prominent full-width banner at the top of the page whenever the outline is
// generating - visible without scrolling, so a shop watching the page never
// mistakes a 60-120s job for a broken one. During a redraw the template
// card's own picker and the placement editor both stay usable underneath.
function OutlineProgressBanner({ outline, vehicleLabel, isRedraw }: {
  outline: NonNullable<PrintTemplate['outline']>;
  vehicleLabel: string;
  isRedraw: boolean;
}) {
  return (
    <Card aria-live="polite">
      <CardContent className="py-4">
        <OutlineProgressContent outline={outline} vehicleLabel={vehicleLabel} isRedraw={isRedraw} />
      </CardContent>
    </Card>
  );
}

// Same top banner slot, but for a failed first generation (the only status
// that reaches 'failed' - a redraw failure instead restores the previous
// 'ready' outline with an error note, handled by the toast above). A first
// generation is always free to retry since nothing has been drawn yet.
function OutlineErrorBanner({ outline, onRetry, retrying }: {
  outline: NonNullable<PrintTemplate['outline']>;
  onRetry: () => void;
  retrying: boolean;
}) {
  const { t } = useTranslation('printWraps');
  const free = outline.free ?? true;
  return (
    <Card aria-live="polite">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-bold text-danger">{t('template.outline.failed')}</p>
          {outline.error ? <p className="text-xs text-muted">{outline.error}</p> : null}
          {!free ? <p className="text-xs text-muted">{t('template.outline.error.refunded')}</p> : null}
        </div>
        <Button size="sm" variant="outline" loading={retrying} onClick={onRetry}>
          <RefreshCw size={13} className="mr-1" /> {free ? t('template.outline.error.retryFree') : t('template.outline.error.retry')}
        </Button>
      </CardContent>
    </Card>
  );
}

// Shown in place of the placement editor for the first generation only (there
// is no previous outline to keep designing on) - mirrors whatever the top
// banner is currently showing so this area is never left empty while the
// vehicle has nothing drawn yet.
function OutlineFirstGenPlaceholder({ outline, vehicleLabel, onRetry, retrying }: {
  outline: PrintTemplate['outline'];
  vehicleLabel: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  const { t } = useTranslation('printWraps');
  if (outline?.status === 'failed') {
    const free = outline.free ?? true;
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertTriangle size={28} className="text-danger" aria-hidden />
          <p className="text-sm font-bold text-ink">{t('template.outline.failed')}</p>
          {outline.error ? <p className="max-w-md text-xs text-muted">{outline.error}</p> : null}
          <Button size="sm" variant="outline" loading={retrying} onClick={onRetry}>
            <RefreshCw size={13} className="mr-1" /> {free ? t('template.outline.error.retryFree') : t('template.outline.error.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (outline?.status === 'pending') {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-16">
          <div className="w-full max-w-md" aria-live="polite">
            <OutlineProgressContent outline={outline} vehicleLabel={vehicleLabel} isRedraw={false} />
          </div>
        </CardContent>
      </Card>
    );
  }
  // Undefined or 'declined' - the auto-start effect's POST hasn't resolved
  // (or hasn't landed) yet, so there is no progress data to show at all.
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <Loader2 size={28} className="text-accent motion-safe:animate-spin" aria-hidden />
        <p className="text-sm font-bold text-ink">{t('template.outline.pending.title', { vehicle: vehicleLabel })}</p>
      </CardContent>
    </Card>
  );
}

// Fullscreen render viewer, same look as the New Quote photo lightbox: black
// backdrop, click outside the image or press Escape to close.
function RenderLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  const { t } = useTranslation('printWraps');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('generate.closeRender')}
        className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white"
      >
        <X size={20} />
      </button>
      <img src={url} alt="" onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain" />
    </div>
  );
}
